import { logger } from "@/utils/logger";
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { cn } from '@/lib/utils';

interface TimeSlot {
  value: string;
  label: string;
  time?: string; // For service form display
}

interface IsolatedTimePickerProps {
  value?: string;
  onChange?: (time: string) => void;
  timeSlots: TimeSlot[];
  placeholder?: string;
  className?: string;
  variant?: 'dropdown' | 'buttons'; // dropdown for appointment, buttons for service
  loading?: boolean;
  error?: boolean;
}

export const IsolatedTimePicker: React.FC<IsolatedTimePickerProps> = ({
  value,
  onChange,
  timeSlots,
  placeholder = "Select time",
  className,
  variant = 'dropdown',
  loading = false,
  error = false,
}) => {
  logger.log('🔴 TimePicker render - error:', error, 'value:', value);
  const [isOpen, setIsOpen] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.d2cbot-isolated-timepicker')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleTimeSelect = (timeValue: string) => {
    onChange?.(timeValue);
    if (variant === 'dropdown') {
      setIsOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    const selectedSlot = timeSlots.find(slot => slot.value === value);
    return selectedSlot ? selectedSlot.label : value;
  };

  if (loading) {
    return (
      <div className={cn("d2cbot-isolated-timepicker relative", className)}>
        <div className="flex items-center justify-center h-7 bg-gray-50 rounded border border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            <span className="text-xs text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'buttons') {
    return (
      <div className={cn("d2cbot-isolated-timepicker", className)}>
        <div className="d2cbot-time-buttons-grid flex flex-row gap-px w-full">
          {timeSlots.map((slot) => (
            <button
              key={slot.value}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleTimeSelect(slot.value);
              }}
              className={cn(
                "d2cbot-time-button flex-1 h-12 text-base flex flex-col justify-center items-center",
                "border border-gray-200 rounded-sm transition-colors",
                value === slot.value
                  ? "bg-black text-white border-black"
                  : "bg-white text-black hover:bg-gray-50"
              )}
            >
              <div className="font-medium leading-tight text-center">{slot.label}</div>
              {slot.time && (
                <div className="text-[9px] opacity-70 leading-tight text-center">{slot.time}</div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Dropdown variant (for appointment form)
  return (
    <div className={cn("d2cbot-isolated-timepicker relative", className)}>
      <Button
        type="button"
        variant="outline"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={cn(
          "d2cbot-time-trigger w-full justify-between text-left font-normal h-7 text-sm",
          !value && "text-gray-500"
        )}
      >
        <div className="flex items-center">
          <FontAwesomeIcon icon={faClock} className="mr-2 h-3 w-3" />
          {getDisplayValue()}
        </div>
        <ChevronDown className="h-3 w-3 opacity-50" />
      </Button>

      {isOpen && (
        <div
          className={cn(
            "d2cbot-time-popup absolute bottom-full left-0 mb-1 z-[99999]",
            "bg-white border border-gray-200 rounded-md shadow-lg",
            "w-full max-h-48 overflow-y-auto"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '0',
            marginBottom: '4px',
            zIndex: 99999,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: '100%',
            maxHeight: '192px',
            overflowY: 'auto'
          }}
        >
          <div className="d2cbot-time-options py-1">
            {timeSlots.map((slot) => (
              <button
                key={slot.value}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTimeSelect(slot.value);
                }}
                className={cn(
                  "d2cbot-time-option w-full px-3 py-2 text-sm text-left",
                  "hover:bg-gray-100 transition-colors",
                  value === slot.value && "bg-gray-100 font-medium"
                )}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};